import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef } from "react";
import Api from "../../services/Api";
import { CoachInterface } from "../../interfaces/common";
import { ClientInterface } from "../../interfaces/common";
import { ChatInterface } from "../../interfaces/common";
import { User } from "flowbite-react-icons/outline";
import { dateFormatSoon } from "../../helpers/dateFunctions";
import WebSocketClient from "../../services/WebSocketClient";
import { getLanguage } from "../../lang/helpers";
import ChatInput from "../ChatInput/ChatInput";

interface Props {
  clientId?: string;
  coachId?: string;
  isCoach: boolean;
}

const Chat: React.FC<Props> = ({ clientId, coachId, isCoach }) => {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [chats, _setChats] = useState<Array<ChatInterface>>([]);
  const chatsRef = useRef<Array<ChatInterface>>([]);
  const [client, setClient] = useState<ClientInterface>();
  const [coach, setCoach] = useState<CoachInterface>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const setChats = (newChats: Array<ChatInterface>) => {
    chatsRef.current = newChats;
    _setChats(newChats);
  };

  const webSocketClient = useRef(WebSocketClient.getInstance());

  useEffect(() => {
    const url = clientId ? "coach/chat/" + clientId + "/get" : "client/chat/" + coachId + "/get";
    api.get(url).then((result) => {
      const chats: Array<ChatInterface> = [];
      for (const chat of result.json.chats as Array<ChatInterface>) {
        chat.dateSent = new Date(chat.dateSent);
        if (chat.dateRead) {
          chat.dateRead = new Date(chat.dateRead);
        }
        chats.push(chat);
      }
      setClient(result.json.client);
      setCoach(result.json.coach);
      setChats(chats);
      setLoaded(true);
      webSocketClient.current.webSocket?.addEventListener(
        "message",
        messageListener
      );
    });

    return () => {
      webSocketClient.current.webSocket?.removeEventListener(
        "message",
        messageListener
      );
    };
  }, []);

  const sendReadAll = () => {
    const url = "client/chat/" + coachId + "/read_all";
    api.get(url).then(() => console.log("sent read"));
  };

  const messageListener = (event: any) => {
    const mess = JSON.parse(event.data);
    const fromId = isCoach ? "client_" + clientId : "coach_" + coachId;
    if (mess.chat && mess.fromId === fromId) {
      const newChat: ChatInterface = {
        id: Math.floor(Math.random() * 1000000),
        text: mess.chat.message,
        sentByCoach: false,
        dateSent: new Date(),
        dateRead: new Date(),
      };
      const newChats = [...chatsRef.current];
      newChats.push(newChat);
      setChats(newChats);
      sendReadAll();
    }
  };

  useEffect(() => {
    if (chats.length > 0 && chatWindowRef.current) {
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
    }
  }, [chats]);

  if (!loaded) {
    return <div className="mt-4">Loading chats...</div>;
  }

  const send = (text: string) => {
    if (text.length < 1) {
      return;
    }

    const url = isCoach
      ? "coach/chat/" + clientId + "/send"
      : "client/chat/" + coachId + "/send";
    const data = {
      text: text,
      sentByCoach: isCoach,
    };
    api.post(url, data).then((result) => {
      if (result.response.ok) {
        const chat: ChatInterface = {
          id: 0,
          text: text,
          sentByCoach: isCoach,
          dateSent: new Date(),
        };
        if (result.json.readBy && result.json.readBy > 0) {
          chat.dateRead = new Date();
        }
        const newChats = [...chats];
        newChats.push(chat);
        setChats(newChats);
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
        }

      }
    });
  };

  const chatInputHeightChange = (height: number) => {
    chatWindowRef.current?.scrollTo(0, chatWindowRef.current.scrollHeight);
  };

  return (
    <div className="h-full relative flex justify-between flex-col">
      <div ref={chatWindowRef} className="overflow-y-scroll">
        {chats.map((chat: ChatInterface, index) => {
          const picture = chat.sentByCoach
            ? (coach && coach.picture)
              ? coach.picture
              : null
            : (client && client.picture)
            ? client.picture
            : null;
          const name = chat.sentByCoach
            ? coach?.firstName + " " + coach?.lastName
            : client?.firstName + " " + client?.lastName;
          return (
            <div className="px-4 mt-4 flex" key={"chat_" + index}>
              {picture ? (
                <img className="picture-sm" src={picture} alt="pic" />
              ) : (
                <User className="picture-sm" aria-hidden="true" />
              )}
              <div className="ml-4">
                <div className="flex">
                  <div className="font-medium">{name}</div>
                  <span className="text-gray-400 ml-4 text-sm">
                    {dateFormatSoon(chat.dateSent, lang)}
                  </span>
                  <span className="text-gray-400 ml-4 text-sm">
                    {lang === "sv" ? "Läst" : "Read"}:&nbsp;
                    {chat.dateRead ? dateFormatSoon(chat.dateRead, lang) : "-"}
                  </span>
                </div>
                <div className="text-gray-300 whitespace-pre-line">
                  {chat.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <hr className="border-indigo-800 mt-4" />
        <ChatInput
          handleSendMessage={send}
          onHeightChange={chatInputHeightChange}
        />
        {/*<div className="flex justify-between items-center p-4">*/}
        {/*<textarea value={text} className="z-input w-full h-20"*/}
        {/*          onKeyUp={(e) => maybeSend(e)}*/}
        {/*          onChange={(e) => setText(e.target.value)}*/}
        {/*          placeholder={lang === 'sv' ? "Skriv meddelande här..." : "Write message here..."}/>*/}
        {/*  <img src={sendIcon} alt="send" className="ml-4 picture-sm cursor-pointer" onClick={() => send()}/>*/}
        {/*  */}
        {/*</div>*/}
      </div>
    </div>
  );
};

export default Chat;
